import { PATIENT_LIST_NAV_SPOTLIGHT_MODAL_ID } from '@/atoms/patient-list-modal-ids';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

interface PatientListNavSpotlightProps {
  /** Navigates to the Patients list. Fired by the "Take a look" CTA. */
  onShowMe?: () => void;
  children?: ReactNode;
}

/**
 * Patients sidebar spotlight — shown once, two days after the user saw the
 * {@link PatientListIntroductionModal} (the delay is computed in
 * `GlobalModalsWrapper` from the intro modal's `seen_modals.first_seen_at`).
 *
 * Visibility is owned by the modal queue (`ModalId.PATIENT_LIST_NAV_SPOTLIGHT`):
 * `launchModalsConfigAtom` orders it against other launch modals and `seen_modals`
 * keeps it to once per user. Anchors to the Patients nav item via `children`.
 */
export const PatientListNavSpotlight = ({
  onShowMe,
  children,
}: PatientListNavSpotlightProps) => {
  const { isOpen, handleClose } = useQueuedModal(PATIENT_LIST_NAV_SPOTLIGHT_MODAL_ID);
  const anchorRef = useRef<HTMLDivElement>(null);
  const dismissedRef = useRef(false);
  const viewTrackedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !viewTrackedRef.current) {
      viewTrackedRef.current = true;
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.ModalViewed,
        props: { screen_name: ScreenName.PatientListNavSpotlight },
      });
    }
  }, [isOpen]);

  // Unlike the Evidence reminder spotlight (whose nav item sits near the top and
  // is always visible), the Patients item sits lower in the scrollable sidebar
  // and can be below the fold when the spotlight opens. Bring it into view so the
  // popover pins to it instead of clamping to the bottom of the viewport.
  useEffect(() => {
    if (!isOpen) return;
    const frame = requestAnimationFrame(() => {
      anchorRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  const handleDismiss = useCallback(() => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    handleClose();
  }, [handleClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) handleDismiss();
    },
    [handleDismiss]
  );

  const handleShowMeClick = useCallback(() => {
    onShowMe?.();
    handleDismiss();
  }, [onShowMe, handleDismiss]);

  if (!isOpen) return <>{children}</>;

  return (
    <Spotlight open={isOpen} onOpenChange={handleOpenChange}>
      {children && <SpotlightAnchor ref={anchorRef}>{children}</SpotlightAnchor>}
      <SpotlightContent
        align="start"
        alignOffset={-4}
        side="right"
        sideOffset={12}
        // Re-pin to the Patients nav item every frame. The default "optimized"
        // strategy measures once and only re-pins on scroll/resize, so when the
        // spotlight opens right as the sidebar is still settling (e.g. straight
        // after the "View patients" navigation) it lands low and then jumps up.
        updatePositionStrategy="always"
        className="w-62 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border-0 p-0 shadow-lg"
        data-testid="patient-list-nav-spotlight"
      >
        <SpotlightArrow width={12} height={8} />
        <SpotlightHeader className="rounded-t-xl px-4 pt-3">
          <SpotlightTitle className="flex-1">
            <FormattedMessage defaultMessage="Find your patients here" id="fKAjAVM5iv" />
          </SpotlightTitle>
          <SpotlightClose />
        </SpotlightHeader>
        <SpotlightDescription className="p-0 px-4 text-start">
          <FormattedMessage
            defaultMessage="Open Patients any time to see everyone you have worked with and pick up where you left off."
            id="AvJwC3IaHZ"
          />
        </SpotlightDescription>
        <SpotlightFooter className="justify-end rounded-b-xl px-4 pb-3">
          <Button
            variant="secondary"
            size="xs"
            onClick={handleShowMeClick}
            className="bg-surface text-text-primary hover:bg-background-secondary"
          >
            <FormattedMessage defaultMessage="Take a look" id="dMK6UbTR3x" />
          </Button>
        </SpotlightFooter>
      </SpotlightContent>
    </Spotlight>
  );
};
